import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const ModuleContext = createContext();

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
};

export const ModuleProvider = ({ children }) => {
  const [modules, setModules] = useState({});
  const [effectiveModules, setEffectiveModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getToken = () => localStorage.getItem('token');

  const fetchModuleConfig = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/module-config/effective`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setModules(response.data.modules || {});
      setEffectiveModules(response.data.effective_modules || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch module config:', err);
      // On error, enable all modules as fallback
      setEffectiveModules(['crm', 'marketing', 'sales', 'service', 'finance', 'hr', 'operations', 'tools', 'reports']);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModuleConfig();
  }, [fetchModuleConfig]);

  // Check if a specific module is enabled
  const isModuleEnabled = useCallback((moduleKey) => {
    return effectiveModules.includes(moduleKey);
  }, [effectiveModules]);

  // Check if multiple modules (any) are enabled
  const hasAnyModule = useCallback((moduleKeys) => {
    return moduleKeys.some(key => effectiveModules.includes(key));
  }, [effectiveModules]);

  // Check if all specified modules are enabled
  const hasAllModules = useCallback((moduleKeys) => {
    return moduleKeys.every(key => effectiveModules.includes(key));
  }, [effectiveModules]);

  // Refresh module config (call after admin changes)
  const refreshModules = useCallback(() => {
    setLoading(true);
    fetchModuleConfig();
  }, [fetchModuleConfig]);

  const value = {
    modules,
    effectiveModules,
    loading,
    error,
    isModuleEnabled,
    hasAnyModule,
    hasAllModules,
    refreshModules
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
};

export default ModuleProvider;
